import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useApp } from '../../context/AppContext';
import { MawLogoFull } from '../shared/MawLogo';
import { MawLogoIcon } from '../shared/MawLogo';
import { hasFeature, PLAN_LABEL, getTrialStatus } from '../../utils/license';
import { NAV_GROUPS } from '../../navigation';
import { NovidadesModal } from '../WhatsNew/Novidades';

const nav = NAV_GROUPS;


const notifStyle = {
  warning: { icon: 'fa-triangle-exclamation', color: '#f97316' },
  info:    { icon: 'fa-circle-info',           color: '#378add' },
  success: { icon: 'fa-check-circle',          color: '#1d9e75' },
  error:   { icon: 'fa-circle-exclamation',    color: '#e24b4a' },
};

function relativeTime(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1)  return 'agora';
  if (mins < 60) return `${mins}min`;
  const h = Math.floor(mins / 60);
  if (h < 24)    return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function Sidebar() {
  const { state, dispatch, unreadNotifs } = useApp();
  const [notifOpen, setNotifOpen] = useState(false);
  const [novidadesOpen, setNovidadesOpen] = useState(false);
  const [lockedAlert, setLockedAlert] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('maw_sidebar_collapsed') === '1');

  // Grupos recolhidos do menu (persistido) — clicar no título do grupo abre/fecha
  const [closedGroups, setClosedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('maw_nav_closed') || '[]'); } catch { return []; }
  });
  const toggleGroup = (group) => setClosedGroups(prev => {
    const next = prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group];
    localStorage.setItem('maw_nav_closed', JSON.stringify(next));
    return next;
  });

  const toggleCollapse = () => setCollapsed(c => {
    const next = !c;
    localStorage.setItem('maw_sidebar_collapsed', next ? '1' : '0');
    return next;
  });

  const plan = state.license?.plan;
  const trial = getTrialStatus(state.user, state.license);

  const go = (id) => {
    if (!hasFeature(plan, id, trial.active)) {
      setLockedAlert(id);
      return;
    }
    dispatch({ type: 'SET_TAB', payload: id });
  };

  const initials = (state.user?.name || 'U')
    .split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      {/* Logo + toggle */}
      <div className="sidebar-logo" style={{ position: 'relative' }}>
        {collapsed ? <MawLogoIcon size={32} /> : <MawLogoFull size={1} />}
        <button
          onClick={toggleCollapse}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          style={{
            position: 'absolute', right: collapsed ? '50%' : 8, bottom: -14,
            transform: collapsed ? 'translateX(50%)' : 'none',
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--sidebar-bg)', border: '1px solid rgba(255,255,255,0.18)',
            color: 'rgba(255,255,255,0.55)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, zIndex: 10, transition: 'all 0.2s',
          }}
        >
          <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'}`}></i>
        </button>
      </div>

      {/* User strip */}
      <div className="sidebar-user-strip" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: collapsed ? '10px 0 12px' : '0 12px 12px', marginBottom: 8,
        borderBottom: '1px solid var(--border)',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}>
        {/* Avatar */}
        <div
          className="sidebar-user-avatar"
          onClick={() => go('profile')}
          style={{
            width: 34, height: 34, borderRadius: '50%', overflow: 'hidden',
            background: 'linear-gradient(135deg,var(--navy),var(--gold))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, cursor: 'pointer',
          }}
        >
          {state.user?.avatar
            ? <img src={state.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>{initials}</span>
          }
        </div>

        {/* Name */}
        {!collapsed && (
          <div className="sidebar-user-name" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {state.user?.name || 'Usuário'}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>Minha conta</div>
          </div>
        )}

        {/* Olho — modo privacidade */}
        {!collapsed && (
          <button
            onClick={() => dispatch({ type: 'TOGGLE_PRIVACY' })}
            title={state.privacyMode ? 'Mostrar valores' : 'Ocultar valores'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              width: 32, height: 32, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'background 0.12s',
            }}
          >
            <i className={`fas ${state.privacyMode ? 'fa-eye-slash' : 'fa-eye'}`} style={{ fontSize: 14, color: state.privacyMode ? 'var(--gold)' : 'var(--muted)' }}></i>
          </button>
        )}

        {/* Novidades */}
        {!collapsed && (
          <button
            onClick={() => setNovidadesOpen(true)}
            title="Novidades"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              width: 32, height: 32, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'background 0.12s',
            }}
          >
            <i className="fas fa-sparkles" style={{ fontSize: 14, color: 'var(--muted)' }}></i>
          </button>
        )}

        {/* Bell */}
        {!collapsed && (
          <button
            className="sidebar-user-bell"
            onClick={() => setNotifOpen(o => !o)}
            title="Notificações"
            style={{
              position: 'relative', background: notifOpen ? 'var(--surface2)' : 'none',
              border: 'none', cursor: 'pointer',
              width: 32, height: 32, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'background 0.12s',
            }}
          >
            <i className="fas fa-bell" style={{ fontSize: 14, color: unreadNotifs > 0 ? 'var(--gold)' : 'var(--muted)' }}></i>
            {unreadNotifs > 0 && (
              <span style={{
                position: 'absolute', top: 3, right: 3,
                minWidth: 16, height: 16, borderRadius: 99,
                background: '#e24b4a', color: '#fff',
                fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px', lineHeight: 1,
              }}>
                {unreadNotifs > 9 ? '9+' : unreadNotifs}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">
        {nav.map(section => {
          const isClosed = closedGroups.includes(section.group);
          // Grupo com a aba ativa nunca fica escondido (evita "sumir" a tela atual)
          const hasActive = section.items.some(i => i.id === state.activeTab);
          const showItems = !isClosed || hasActive;
          return (
            <div key={section.group} className="sidebar-section">
              <button
                className="sidebar-group-label sidebar-group-toggle"
                onClick={() => toggleGroup(section.group)}
                title={isClosed ? 'Expandir' : 'Recolher'}
              >
                <span style={{ flex: 1, textAlign: 'left' }}>{section.group}</span>
                {!collapsed && <i className={`fas fa-chevron-${showItems ? 'down' : 'right'}`} style={{ fontSize: 8, opacity: 0.5 }}></i>}
              </button>
              {showItems && section.items.map(item => {
                const locked = !hasFeature(plan, item.id, trial.active);
                return (
                  <button
                    key={item.id}
                    className={`sidebar-item ${state.activeTab === item.id ? 'active' : ''} ${locked ? 'sidebar-item-locked' : ''}`}
                    onClick={() => go(item.id)}
                    title={locked ? 'Disponível no plano Completo' : undefined}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {locked && <i className="fas fa-lock" style={{ fontSize: 10, opacity: 0.5 }}></i>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Locked feature modal */}
      {lockedAlert && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setLockedAlert(null)}
        >
          <div
            style={{ background: '#132236', border: '1px solid #1e3a5f', borderRadius: 16, padding: '32px 28px', maxWidth: 360, width: '100%', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#fff', marginBottom: 8 }}>Recurso bloqueado</div>
            <div style={{ color: '#a0b4c8', fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
              {plan
                ? <>Este módulo não está incluído no plano <strong style={{ color: 'var(--gold)' }}>{PLAN_LABEL[plan]}</strong>.<br />Faça upgrade para o plano <strong style={{ color: 'var(--gold)' }}>Pro</strong> ou <strong style={{ color: 'var(--gold)' }}>Família</strong> para desbloquear.</>
                : <>Ative uma licença para acessar este módulo.</>
              }
            </div>
            <button
              onClick={() => setLockedAlert(null)}
              style={{ width: '100%', padding: '11px', background: 'var(--gold)', color: '#0d1b2a', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Bottom — trial counter or plan */}
      <div className="sidebar-bottom">
        {plan ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(var(--gold-rgb),0.12)', borderRadius: 8,
            padding: '8px 12px', border: '1px solid rgba(var(--gold-rgb),0.25)',
          }}>
            <i className="fas fa-medal" style={{ fontSize: 12, color: 'var(--gold)' }}></i>
            <span style={{ fontSize: 12, color: 'var(--gold)', fontWeight: 600 }}>Plano {PLAN_LABEL[plan]}</span>
          </div>
        ) : trial.active ? (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: trial.daysLeft <= 2 ? 'rgba(226,75,74,0.13)' : 'rgba(29,158,117,0.13)',
              borderRadius: 10, padding: '10px 12px',
              border: `1px solid ${trial.daysLeft <= 2 ? 'rgba(226,75,74,0.35)' : 'rgba(29,158,117,0.35)'}`,
            }}>
              {/* Circular countdown */}
              <div style={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
                <svg width="36" height="36" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15" fill="none"
                    stroke={trial.daysLeft <= 2 ? '#e24b4a' : '#1d9e75'} strokeWidth="3"
                    strokeDasharray={`${(trial.daysLeft / 7) * 94.25} 94.25`}
                    strokeLinecap="round"
                  />
                </svg>
                <span style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                  color: trial.daysLeft <= 2 ? '#e24b4a' : '#1d9e75',
                }}>{trial.daysLeft}</span>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: trial.daysLeft <= 2 ? '#e24b4a' : '#1d9e75', lineHeight: 1.3 }}>
                  {trial.daysLeft === 1 ? 'Último dia!' : `${trial.daysLeft} dias restantes`}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Período de teste</div>
              </div>
            </div>
            {trial.daysLeft <= 2 && (
              <a
                href="https://www.financasmaw.com.br/plano.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block', marginTop: 8, padding: '9px 12px',
                  background: '#e24b4a', color: '#fff',
                  borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 700, textAlign: 'center',
                  textDecoration: 'none', letterSpacing: 0.2,
                }}
              >
                <i className="fas fa-crown" style={{ marginRight: 6 }}></i>
                Ver planos e preços
              </a>
            )}
          </>
        ) : null}
      </div>

      {/* Notifications panel — portal para escapar do stacking context da sidebar */}
      {notifOpen && ReactDOM.createPortal(
        <>
          {/* backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 8999 }}
            onClick={() => setNotifOpen(false)}
          />

          {/* panel */}
          <div style={{
            position: 'fixed', left: collapsed ? 60 : 240, top: 0, bottom: 0, width: 320,
            background: 'var(--card-bg)',
            boxShadow: '6px 0 28px rgba(0,0,0,0.18)',
            borderLeft: '1px solid var(--border)',
            zIndex: 9000,
            display: 'flex', flexDirection: 'column',
            animation: 'slideInLeft 0.2s ease',
          }}>
            {/* Panel header */}
            <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Notificações</span>
                  {unreadNotifs > 0 && (
                    <span style={{
                      padding: '2px 8px', borderRadius: 99,
                      background: '#e24b4a', color: '#fff',
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {unreadNotifs}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {unreadNotifs > 0 && (
                    <button
                      onClick={() => dispatch({ type: 'CLEAR_NOTIFICATIONS' })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--blue)', fontWeight: 500 }}
                    >
                      Marcar todas como lidas
                    </button>
                  )}
                  {state.notifications.length > 0 && (
                    <button
                      onClick={() => dispatch({ type: 'DELETE_ALL_NOTIFICATIONS' })}
                      title="Apagar todas as notificações"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', lineHeight: 1, padding: '0 2px' }}
                    >
                      <i className="fas fa-trash-can"></i>
                    </button>
                  )}
                  <button
                    onClick={() => setNotifOpen(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--muted)', lineHeight: 1, padding: '0 2px' }}
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {state.notifications.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, gap: 12, color: 'var(--muted)' }}>
                  <i className="fas fa-bell-slash" style={{ fontSize: 34, opacity: 0.35 }}></i>
                  <div style={{ fontSize: 13 }}>Nenhuma notificação</div>
                </div>
              ) : (
                state.notifications.map(n => {
                  const s = notifStyle[n.type] || { icon: 'fa-bell', color: 'var(--muted)' };
                  return (
                    <div
                      key={n.id}
                      onClick={() => dispatch({ type: 'MARK_NOTIF_READ', payload: n.id })}
                      style={{
                        display: 'flex', gap: 12, padding: '12px 20px',
                        background: n.read ? 'transparent' : `${s.color}0d`,
                        borderLeft: `3px solid ${n.read ? 'transparent' : s.color}`,
                        cursor: 'pointer', transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = n.read ? 'transparent' : `${s.color}0d`; }}
                    >
                      <div style={{
                        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                        background: `${s.color}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={`fas ${s.icon}`} style={{ color: s.color, fontSize: 13 }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                          <div style={{ fontWeight: n.read ? 500 : 700, fontSize: 12, color: 'var(--text)', lineHeight: 1.4 }}>
                            {n.title}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>
                            {relativeTime(n.createdAt)}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3, lineHeight: 1.5 }}>
                          {n.message}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
                {state.notifications.length} notificação{state.notifications.length !== 1 ? 'ões' : ''} · Apenas do sistema
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
      {novidadesOpen && ReactDOM.createPortal(
        <NovidadesModal onClose={() => setNovidadesOpen(false)} />,
        document.body
      )}
    </aside>
  );
}
